import React, { useEffect, useState } from 'react';
import { Download, X, CheckCircle2 } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
type DownloadEvent = {
  name: string;
  city: string;
  state: string;
  minutesAgo: number;
};
const DOWNLOAD_EVENTS: DownloadEvent[] = [{
  name: 'Carlos M.',
  city: 'Houston',
  state: 'TX',
  minutesAgo: 2
}, {
  name: 'José R.',
  city: 'Phoenix',
  state: 'AZ',
  minutesAgo: 4
}, {
  name: 'Andrés P.',
  city: 'Miami',
  state: 'FL',
  minutesAgo: 6
}, {
  name: 'Luis G.',
  city: 'Dallas',
  state: 'TX',
  minutesAgo: 9
}, {
  name: 'Miguel A.',
  city: 'Los Angeles',
  state: 'CA',
  minutesAgo: 11
}, {
  name: 'Roberto S.',
  city: 'Chicago',
  state: 'IL',
  minutesAgo: 14
}, {
  name: 'Fernando V.',
  city: 'San Antonio',
  state: 'TX',
  minutesAgo: 17
}, {
  name: 'Diego H.',
  city: 'Orlando',
  state: 'FL',
  minutesAgo: 22
}, {
  name: 'Javier T.',
  city: 'Las Vegas',
  state: 'NV',
  minutesAgo: 25
}, {
  name: 'Ricardo L.',
  city: 'Atlanta',
  state: 'GA',
  minutesAgo: 31
}, {
  name: 'Eduardo M.',
  city: 'Charlotte',
  state: 'NC',
  minutesAgo: 35
}, {
  name: 'Sebastián C.',
  city: 'San Diego',
  state: 'CA',
  minutesAgo: 42
}, {
  name: 'Pablo N.',
  city: 'Denver',
  state: 'CO',
  minutesAgo: 48
}, {
  name: 'Daniel R.',
  city: 'Sacramento',
  state: 'CA',
  minutesAgo: 53
}, {
  name: 'Alejandro F.',
  city: 'Tampa',
  state: 'FL',
  minutesAgo: 58
}];
const AVATAR_COLORS = ['bg-emerald-500/20 border-emerald-500/40 text-emerald-300', 'bg-amber-500/20 border-amber-500/40 text-amber-300', 'bg-blue-500/20 border-blue-500/40 text-blue-300', 'bg-purple-500/20 border-purple-500/40 text-purple-300', 'bg-rose-500/20 border-rose-500/40 text-rose-300', 'bg-cyan-500/20 border-cyan-500/40 text-cyan-300'];
const formatTimeAgo = (m: number) => {
  if (m < 1) return 'justo ahora';
  if (m === 1) return 'hace 1 minuto';
  if (m < 60) return `hace ${m} minutos`;
  const h = Math.floor(m / 60);
  return h === 1 ? 'hace 1 hora' : `hace ${h} horas`;
};
const getInitials = (fullName: string) => fullName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
export function DownloadNotification() {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (dismissed) return;
    // Initial delay before first appearance
    const initialTimeout = setTimeout(() => setVisible(true), 4000);
    return () => clearTimeout(initialTimeout);
  }, [dismissed]);
  useEffect(() => {
    if (dismissed || !visible) return;
    // Hide after 5s, swap, show again after a pause
    const hideTimer = setTimeout(() => setVisible(false), 5000);
    return () => clearTimeout(hideTimer);
  }, [visible, index, dismissed]);
  useEffect(() => {
    if (dismissed || visible) return;
    const swapTimer = setTimeout(() => {
      setIndex(i => (i + 1) % DOWNLOAD_EVENTS.length);
      setVisible(true);
    }, 3500);
    return () => clearTimeout(swapTimer);
  }, [visible, dismissed]);
  if (dismissed) return null;
  const event = DOWNLOAD_EVENTS[index];
  const colorClass = AVATAR_COLORS[index % AVATAR_COLORS.length];
  return <div className="fixed bottom-4 left-4 z-50 pointer-events-none max-w-[calc(100vw-2rem)]" data-id="element-211">
      <AnimatePresence mode="wait" data-id="element-212">
        {visible && <motion.div key={index} initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.97
      }} transition={{
        duration: 0.35,
        ease: [0.22, 1, 0.36, 1]
      }} className="pointer-events-auto" data-id="element-213">
            <div className="relative flex items-center gap-3 bg-[#0f1b35]/95 backdrop-blur-xl border border-amber-500/30 rounded-2xl pl-3 pr-9 py-2.5 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.7),0_0_30px_-15px_rgba(245,158,11,0.4)] max-w-xs sm:max-w-sm" data-id="element-214">
              {/* Avatar */}
              <div className={`relative w-10 h-10 rounded-full border flex items-center justify-center font-black text-xs flex-shrink-0 ${colorClass}`} data-id="element-215">
                {getInitials(event.name)}
                <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-500 border-2 border-[#0f1b35] flex items-center justify-center" data-id="element-216">
                  <CheckCircle2 className="w-2.5 h-2.5 text-white" strokeWidth={3} data-id="element-217" />
                </div>
              </div>

              {/* Text */}
              <div className="min-w-0 flex-1" data-id="element-218">
                <p className="text-[11px] sm:text-xs text-white leading-tight" data-id="element-219">
                  <span className="font-bold" data-id="element-220">{event.name}</span>{' '}
                  <span className="text-gray-400" data-id="element-221">
                    de {event.city}, {event.state}
                  </span>
                </p>
                <p className="text-[10px] sm:text-[11px] text-amber-400 font-semibold mt-0.5 flex items-center gap-1 leading-tight" data-id="element-222">
                  <Download className="w-2.5 h-2.5" data-id="element-223" />
                  Descargó la guía · {formatTimeAgo(event.minutesAgo)}
                </p>
              </div>

              {/* Close */}
              <button type="button" onClick={() => setDismissed(true)} aria-label="Cerrar notificación" className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full text-gray-500 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors" data-id="element-224">
                <X className="w-3 h-3" data-id="element-225" />
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}